/**
 * SSI Conflict Alert Component
 * 
 * This component displays a dialog when a conflict is detected between the SSI clinician's
 * recommendations and the Lead clinician's CI/DD decisions in the 3 Borough / SLaM workflow.
 * 
 * The dialog:
 * 1. Explains the conflict to the Lead clinician
 * 2. Shows a summary of both decisions (SSI recommendation vs Lead's decision)
 * 3. Provides option to view the approved SSI report
 * 4. Informs Lead that proceeding will reset SSI for re-editing to ensure consistency
 * 
 * The more common case handled: SSI says "Not Met" but Lead identifies ADHD.
 */

import React from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { ExclamationIcon, DocumentTextIcon, ChevronRightIcon } from '@heroicons/react/outline';

/**
 * SSIConflictAlert - Dialog shown when Lead clinician's decision conflicts with SSI recommendation
 * 
 * @param {boolean} isOpen - Whether the dialog is open
 * @param {function} onClose - Callback when dialog is closed
 * @param {Object} conflictDetails - Details about the conflict from detectSSIConflict()
 * @param {function} onViewSSIReport - Callback to view the SSI report
 * @param {function} onResetSSI - Callback to trigger SSI reset and proceed
 * @param {boolean} isResetting - Whether reset is in progress
 */
export const SSIConflictAlert = ({
  isOpen,
  onClose,
  conflictDetails,
  onViewSSIReport,
  onResetSSI,
  isResetting = false,
}) => {
  // Extract details from conflictDetails.
  // isFlatDifferentials drives label changes: flat DF orgs have no PPD,
  // so we show "Diagnostic Formulation" instead of "Provisional Primary Diagnosis".
  const {
    ssiRecommendation,
    currentDecision,
    conflictDescription,
    ssiClinician,
    leadClinician,
    isFlatDifferentials,
  } = conflictDetails || {};

  const ssiClinicianName = ssiClinician?.clinicianName || 'the SSI clinician';
  const leadClinicianName = leadClinician?.clinicianName || 'Lead Clinician';

  // Handle proceed button click - triggers reset directly
  const handleProceed = () => {
    onResetSSI?.();
  };

  // Handle dialog close
  const handleClose = () => {
    onClose?.();
  };

  return (
    <AlertDialog.Root open={isOpen} onOpenChange={handleClose}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/50 backdrop-blur-sm" />
        <AlertDialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-6 w-[650px] max-w-[90vw] max-h-[85vh] overflow-y-auto shadow-xl">
          {/* Header with warning icon */}
          <div className="flex items-start gap-4 mb-4">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
              <ExclamationIcon className="w-7 h-7 text-amber-600" />
            </div>
            <div className="flex-1">
              <AlertDialog.Title className="text-xl font-bold text-gray-900">
                Conflict Detected
              </AlertDialog.Title>
              <AlertDialog.Description className="text-sm text-gray-600 mt-1">
                {isFlatDifferentials
                  ? `Your diagnostic formulation differs from ${ssiClinicianName}'s recommended diagnosis for ADHD.`
                  : `Your provisional primary diagnosis differs from ${ssiClinicianName}'s recommended diagnosis for ADHD.`}
              </AlertDialog.Description>
            </div>
          </div>

          {/* Conflict explanation */}
          <div className="mb-6">
            <p className="text-gray-700">
              {conflictDescription}
            </p>
          </div>

          {/* Decision comparison */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            {/* SSI Recommendation */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Recommended Diagnosis for ADHD
              </h4>
              <p className="text-lg font-semibold text-gray-900">
                {ssiRecommendation || 'Unknown'}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                By {ssiClinicianName}
              </p>
            </div>

            {/* Current Decision */}
            <div className="bg-violet-50 rounded-lg p-4 border border-violet-200">
              <h4 className="text-sm font-semibold text-violet-600 uppercase tracking-wide mb-2">
                {isFlatDifferentials ? 'Diagnostic Formulation' : 'Provisional Primary Diagnosis'}
              </h4>
              <p className="text-lg font-semibold text-violet-900">
                {currentDecision || 'Unknown'}
              </p>
              <p className="text-sm text-violet-600 mt-1">
                By {leadClinicianName}
              </p>
            </div>
          </div>          
          {/* What happens next explanation — written from the Lead clinician's
              perspective.  The Lead proceeds to DD independently; the supporting
              clinician (SSI clinician) handles the SSI amendments afterwards. */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-amber-800 mb-2">What happens when you proceed:</h4>
            <ul className="text-sm text-amber-700 space-y-2">
              <li>• You will proceed to the Diagnostic Decision, where you can select the criterion threshold based on your own clinical observations.</li>
              <li>• After discussing the outcome with <strong>{ssiClinicianName}</strong>, they will need to amend the list of symptoms and domains of functional impairment information and their recommended diagnosis in the Semi-Structured Interview module.</li>
              <li>• Any paragraphs that have already been approved by <strong>{ssiClinicianName}</strong> will need to be re-reviewed and re-approved before the final patient report can be finalised.</li>
            </ul>
          </div>

          {/* Action buttons */}
          <div className="border-t border-gray-200 pt-4">
            <div className="flex justify-between items-center">
              {/* Cancel button - left side */}
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                disabled={isResetting}
              >
                Cancel
              </button>

              {/* Right side buttons */}
              <div className="flex gap-3">
                {/* View SSI Report button */}
                <button
                  onClick={onViewSSIReport}
                  className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors flex items-center gap-2"
                  disabled={isResetting}
                >
                  <DocumentTextIcon className="w-4 h-4" />
                  <span>View SSI Report</span>
                </button>

                {/* Proceed button - triggers reset then continues to normal flow */}
                <button
                  onClick={handleProceed}
                  className="px-4 py-2 text-sm font-semibold text-white bg-violet-700 rounded-md hover:bg-violet-800 transition-colors flex items-center gap-2"
                  disabled={isResetting}
                >
                  {isResetting ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <span>Proceed to Diagnostic Decision</span>
                      <ChevronRightIcon className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
};

export default SSIConflictAlert;
